/**
 * Barcodes mit der Gerätekamera lesen (Handy, Tablet): Android-Chrome hat den
 * BarcodeDetector eingebaut; sonst (iPhone/Safari, Firefox) liest ZXing als
 * WebAssembly. Die WASM-Datei liegt im eigenen Build und wird erst beim
 * Öffnen der Kamera geladen – nichts geht an fremde Server.
 */
import zxingWasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';

export interface BildLeser {
  lies(quelle: HTMLVideoElement, leinwand: HTMLCanvasElement): Promise<string | null>;
}

interface NativerDetektor {
  detect(quelle: CanvasImageSource): Promise<{ rawValue: string }[]>;
}
interface NativerDetektorKlasse {
  new (opt: { formats: string[] }): NativerDetektor;
  getSupportedFormats(): Promise<string[]>;
}

async function nativerLeser(): Promise<BildLeser | null> {
  const klasse = (globalThis as { BarcodeDetector?: NativerDetektorKlasse }).BarcodeDetector;
  if (klasse === undefined) {
    return null;
  }
  try {
    const formate = await klasse.getSupportedFormats();
    if (!formate.includes('ean_13')) {
      return null;
    }
    const d = new klasse({
      formats: ['ean_13', 'ean_8', 'upc_a'].filter((f) => formate.includes(f)),
    });
    return {
      lies: async (video) => (await d.detect(video))[0]?.rawValue ?? null,
    };
  } catch {
    return null;
  }
}

async function zxingLeser(): Promise<BildLeser> {
  const { prepareZXingModule, readBarcodes } = await import('zxing-wasm/reader');
  prepareZXingModule({
    overrides: {
      locateFile: (pfad: string, praefix: string) =>
        pfad.endsWith('.wasm') ? zxingWasmUrl : praefix + pfad,
    },
  });
  return {
    lies: async (video, leinwand) => {
      const b = video.videoWidth;
      const h = video.videoHeight;
      if (b === 0 || h === 0) {
        return null;
      }
      // Mittlerer Streifen genügt für Strichcodes und spart Rechenzeit.
      const streifenH = Math.round(h * 0.5);
      leinwand.width = b;
      leinwand.height = streifenH;
      const ctx = leinwand.getContext('2d', { willReadFrequently: true });
      if (ctx === null) {
        return null;
      }
      ctx.drawImage(video, 0, (h - streifenH) / 2, b, streifenH, 0, 0, b, streifenH);
      const treffer = await readBarcodes(ctx.getImageData(0, 0, b, streifenH), {
        formats: ['EANUPC'],
        tryHarder: true,
        maxNumberOfSymbols: 1,
      });
      return treffer[0]?.text ?? null;
    },
  };
}

/** Bester verfügbarer Leser: eingebaut, sonst ZXing. */
export async function erzeugeBildLeser(): Promise<BildLeser> {
  return (await nativerLeser()) ?? (await zxingLeser());
}

/** Kurzer Piepton und Vibration als Rückmeldung für einen erkannten Code. */
export function quittiere(): void {
  try {
    navigator.vibrate(60);
  } catch {
    // nicht unterstützt
  }
  try {
    const Klasse =
      (globalThis as { AudioContext?: typeof AudioContext }).AudioContext ??
      (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Klasse === undefined) {
      return;
    }
    const ac = new Klasse();
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.frequency.value = 1800;
    g.gain.value = 0.08;
    o.connect(g).connect(ac.destination);
    o.start();
    o.stop(ac.currentTime + 0.08);
    o.onended = () => {
      void ac.close();
    };
  } catch {
    // ohne Ton weiter
  }
}
